import React, { useEffect, useMemo, useState } from 'react'
import { Card, Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Breadcrumbs from '../../../shared/components/Breadcrumbs';
import { DIVISION, CREATENEW, IMPORTDIVISION, IMPORT, CREATEDIVISION } from '../../../shared/constants/Constants';
import { loadDivisionDataRequest, loadDivisionImportDataRequest } from '../redux/Actions';
import DefaultButton from '../../../shared/components/DefaultButton';
import Modalpopup from '../../../shared/components/Modalpopup';
import DivisionAdd from './DivisionAdd';
import ServerSideGrid from '../../../shared/components/listview/ServerSideGrid';
import { loadCompanyDataRequest } from '../../company/redux/Actions';
import ImportModal from '../../../shared/components/ImportModal';
import Permissions from '../../../shared/components/Permissions';
import { loadCompanyUserGetDataRequest } from '../../company/redux/Actions';


const Division = () => {
    let dispatch = useDispatch();
    let { divisions } = useSelector(state => state.division);
    let { userCompany } = useSelector(state => state.company);

    // Load the user's preferred company on mount
    useEffect(() => {
        dispatch(loadCompanyUserGetDataRequest())
    }, [dispatch])

    // Once we know the user's preferred company (or lack of one), load divisions accordingly
    useEffect(() => {
        const companyIds = userCompany?.data?.company_id;
        if (Array.isArray(companyIds) && companyIds.length > 0) {
            dispatch(loadDivisionDataRequest(companyIds));
        } else {
            dispatch(loadDivisionDataRequest());
        }
    }, [dispatch, userCompany])


    let navigate = useNavigate();

    let [showAddModal, setShowAddModal] = useState(false);
    let [showImportModal, setShowImportModal] = useState(false);

    // Only show active divisions on first load — grid's own filter (Status column) drives this,
    // so the user can change it afterward without us re-slicing the data.
    const defaultActiveFilter = useMemo(() => ({
        filterModel: {
            active: { filterType: 'set', values: ['Active'] },
        },
    }), []);

    // Maps the numeric "active" code to a display label, used for both rendering and the set filter
    const STATUS_LABELS = { 0: 'Active', 1: 'Inactive', 2: 'Upcoming' };

    // Status badge renderer for the "active" code (0 = Active, 1 = Inactive, 2 = Upcoming)
    const statusCellRenderer = (params) => {
        const val = params.value;
        const config = {
            0: { label: 'Active', color: '#198754', bg: 'rgba(25,135,84,0.15)' },
            1: { label: 'Inactive', color: '#dc3545', bg: 'rgba(220,53,69,0.15)' },
            2: { label: 'Upcoming', color: '#0d6efd', bg: 'rgba(13,110,253,0.15)' },
        };
        const { label, color, bg } = config[val] || { label: '-', color: '#666', bg: 'transparent' };
        return (
            <span
                style={{
                    padding: '3px 12px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    color,
                    backgroundColor: bg,
                    fontSize: '13px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '70px',
                    height: '24px',
                }}
            >
                {label}
            </span>
        );
    };

    // Date columns come back as 'mm-dd-yyyy' strings — parse for correct filter comparison
    const dateFilterParams = useMemo(() => ({
        comparator: (filterLocalDateAtMidnight, cellValue) => {
            if (!cellValue) return -1;
            const [month, day, year] = cellValue.split('-').map(Number);
            const cellDate = new Date(year, month - 1, day);
            if (cellDate < filterLocalDateAtMidnight) return -1;
            if (cellDate > filterLocalDateAtMidnight) return 1;
            return 0;
        },
    }), []);

    const columnDefs = useMemo(() => [
        {
            headerName: 'Division Name',
            field: 'division_name',
            sortable: true,
            flex: 1,
            minWidth: 180,
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'Ulti Division Code',
            field: 'ulti_division_code',
            sortable: true,
            width: 180,
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'Company',
            field: 'company_name',
            sortable: true,
            width: 180,
        },
        {
            headerName: 'Start Date',
            field: 'start_date',
            sortable: true,
            width: 140,
            filter: 'agDateColumnFilter',
            filterParams: dateFilterParams,
        },
        {
            headerName: 'End Date',
            field: 'end_date',
            sortable: true,
            width: 140,
            filter: 'agDateColumnFilter',
            filterParams: dateFilterParams,
        },
        {
            headerName: 'Status',
            field: 'active',
            sortable: true,
            width: 130,
            filter: 'agSetColumnFilter',
            filterValueGetter: (params) => STATUS_LABELS[params.data.active] ?? params.data.active,
            filterParams: { values: ['Active', 'Inactive', 'Upcoming'] },
            cellRenderer: statusCellRenderer,
        },
    ], [dateFilterParams]);

    let emp_no = JSON.parse(localStorage.getItem("emp_no"));
    let module_name = "Division";
    let permission = Permissions(emp_no, module_name);

    return (
        <div>
            <Row>
                <Col className="gx-0">
                    <Breadcrumbs
                        children={[
                            { label: DIVISION },
                        ]}
                    />
                </Col>
                <Col xs="auto" className="gx-0">
                    {permission.create_permission ? (
                        <>
                            <DefaultButton buttonClass='default-btn-color me-1' buttonTitle={IMPORT} actionBlock={() => setShowImportModal(true)} />
                            <DefaultButton buttonClass='default-btn-color' buttonTitle={CREATENEW} actionBlock={() => setShowAddModal(true)} />
                        </>
                    ) : (<></>)}
                </Col>
            </Row>
            <Row>
                <Card className='table-card'>
                    <Card.Body>
                        <ServerSideGrid
                            clientSide={true}
                            rowData={divisions || []}
                            columnDefs={columnDefs}
                            externalFilter={defaultActiveFilter}
                            tableHeight="70vh"
                            showPagination={true}
                            paginationPageSize={80}
                            theme="alpine"
                            showExport
                            showExportAll
                            showClearFilter
                            exportFileName={`${DIVISION}_${new Date().toISOString().slice(0, 10)}`}
                            onRowClicked={(row) => {navigate('./' + row.data['id']) }}
                        />
                    </Card.Body>
                </Card>
            </Row>
            <Modalpopup show={showAddModal} handleClose={() => setShowAddModal(false)} actionTitle={CREATEDIVISION}>
                <DivisionAdd handleClose={(reload) => {
                    setShowAddModal(false);
                    if (reload) {
                        dispatch(loadDivisionDataRequest());
                    }
                }} />
            </Modalpopup>
            <ImportModal
                show={showImportModal}
                handleClose={() => setShowImportModal(false)}
                tableName={"division"}
                modalTitle={IMPORTDIVISION}
                importAction={loadDivisionImportDataRequest}
            />

        </div>
    )
}

export default Division